import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Hourglass, Plus, Search, Spline, X } from 'lucide-react';
import { fetchLineasEstado, crearLinea } from 'services/cablesService';
import {
  filtrarLineas, conteoFiltros, normalizarFilaEstado, FILTROS, RESULTADO_PUNTO, rutaLinea, puedeEditarCables,
} from 'lib/cables';
import { etiquetaInstalacion } from 'lib/auditoriaInstalaciones';
import { hoyCR } from 'lib/fechaCR';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import SelectorInstalacion, { useInstalacion } from 'components/SelectorInstalacion';
import ErrorState from 'components/ui/ErrorState';
import EmptyState, { BotonSecundario } from 'components/ui/EmptyState';
import { TarjetaEsqueleto } from 'pages/arboles/TarjetaArbol';
import FormCable from './FormCable';
import TarjetaCable from './TarjetaCable';
import { mensajeError } from 'lib/errores';

const GRILLA = 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4';
const ICONO_CHIP = { vencida: CalendarClock, cambio_vencido: Hourglass };

// Chips de estado: resumen y filtro a la vez. Cada uno dice cuántos cables
// hay; solo aparecen los que tienen alguno (o el que está elegido).
function ChipsEstado({ conteo, valor, onChange }) {
  const chips = FILTROS.filter((f) => f.id === '' || conteo[f.id] > 0 || valor === f.id);
  return (
    <div role="group" aria-label="Filtrar por estado"
      className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
      {chips.map((f) => {
        const activo = valor === f.id;
        const punto = RESULTADO_PUNTO[f.id];
        const Icono = ICONO_CHIP[f.id];
        return (
          <button key={f.id || 'todos'} type="button" aria-pressed={activo} onClick={() => onChange(activo ? '' : f.id)}
            data-testid="chip-estado"
            className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
              activo
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border bg-card text-foreground [@media(hover:hover)]:hover:border-primary/30 [@media(hover:hover)]:hover:bg-muted/60'
            }`}>
            {punto && <span className={`h-2 w-2 rounded-full ${punto}`} aria-hidden="true" />}
            {Icono && <Icono size={13} aria-hidden="true" className="text-muted-foreground" />}
            {f.id === 'sin_resultado' && <span className="h-2 w-2 rounded-full border border-muted-foreground/60" aria-hidden="true" />}
            {f.label}
            <span className={`tabular-nums ${activo ? 'text-primary/80' : 'text-muted-foreground'}`}>{conteo[f.id] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Cables() {
  const { role } = useAuth();
  const puedeEditar = puedeEditarCables(role);
  const navigate = useNavigate();
  const hoy = hoyCR();
  const [instalacion, setInstalacion] = useInstalacion();
  const [datos, setDatos] = useState({ cargando: true, error: null, filas: [] });
  const [filtro, setFiltro] = useState('');
  const [q, setQ] = useState('');
  const [bajas, setBajas] = useState(false);
  const [alta, setAlta] = useState(false);

  const cargar = useCallback(async () => {
    setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      const filas = await fetchLineasEstado();
      setDatos({ cargando: false, error: null, filas });
    } catch (e) {
      setDatos({ cargando: false, error: e, filas: [] });
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const todos = useMemo(() => datos.filas.map((f) => normalizarFilaEstado(f, hoy)), [datos.filas, hoy]);
  const deInstalacion = useMemo(() => filtrarLineas(todos, { instalacion }), [todos, instalacion]);
  const conteo = useMemo(() => conteoFiltros(deInstalacion), [deInstalacion]);
  const lista = useMemo(() => filtrarLineas(todos, { instalacion, filtro, q, incluirBajas: bajas }), [todos, instalacion, filtro, q, bajas]);
  const total = useMemo(() => filtrarLineas(todos, { instalacion, incluirBajas: bajas }).length, [todos, instalacion, bajas]);
  const hayBajas = todos.some((l) => l.activo === false && l.instalacion === instalacion);
  const filtrando = !!(filtro || q.trim());

  const registrar = async (d) => {
    const fila = await crearLinea(d);
    navigate(rutaLinea(fila.id));
  };
  const limpiar = () => { setFiltro(''); setQ(''); };

  return (
    <AppShell titulo="Cables" descripcion={`Los cables del ${etiquetaInstalacion(instalacion)}: datos técnicos, inspecciones y mantenimiento`}>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <SelectorInstalacion value={instalacion} onChange={setInstalacion} mostrarChecklist={false} />
          {puedeEditar && (
            <button type="button" onClick={() => setAlta(true)} disabled={!!datos.error}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold text-primary transition-colors
                hover:bg-primary/[0.07] active:bg-primary/10 disabled:pointer-events-none disabled:opacity-50
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <Plus size={16} strokeWidth={2.25} aria-hidden="true" />Registrar cable
            </button>
          )}
        </div>

        {datos.error ? (
          <ErrorState title="No se pudieron cargar los cables" message={mensajeError(datos.error)} onRetry={cargar} />
        ) : (
          <>
            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
              <label className="relative block sm:w-72 sm:shrink-0">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Código, nombre o plataforma"
                  aria-label="Buscar cable" disabled={datos.cargando}
                  className="h-9 w-full rounded-full border border-border bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground
                    focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60" />
              </label>
              {!datos.cargando && <ChipsEstado conteo={conteo} valor={filtro} onChange={setFiltro} />}
            </div>

            {datos.cargando ? (
              <>
                <p role="status" className="sr-only">Cargando cables…</p>
                <ul className={GRILLA} aria-hidden="true">
                  {Array.from({ length: 8 }, (_, i) => <TarjetaEsqueleto key={i} />)}
                </ul>
              </>
            ) : lista.length === 0 ? (
              <EmptyState framed icon={<Spline size={24} />}
                title={deInstalacion.length ? 'Ningún cable coincide con la búsqueda.' : `Todavía no hay cables registrados en ${etiquetaInstalacion(instalacion)}.`}
                description={!deInstalacion.length && puedeEditar ? 'Use "Registrar cable" para crear la primera ficha.' : undefined}
                action={filtrando ? <BotonSecundario onClick={limpiar}><X size={16} aria-hidden="true" />Quitar filtros</BotonSecundario> : undefined} />
            ) : (
              <ul className={GRILLA} aria-label="Cables">
                {lista.map((l) => <TarjetaCable key={l.id} l={l} />)}
              </ul>
            )}

            {!datos.cargando && (filtrando || hayBajas) && (
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span aria-live="polite">{filtrando && lista.length > 0 ? `${lista.length} de ${total} cables` : ''}</span>
                {hayBajas && (
                  <label className="inline-flex min-h-[36px] cursor-pointer items-center gap-2">
                    <input type="checkbox" checked={bajas} onChange={(e) => setBajas(e.target.checked)}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-primary/30" />
                    Mostrar dados de baja
                  </label>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <FormCable open={alta} onClose={() => setAlta(false)} instalacion={instalacion} onGuardar={registrar} />
    </AppShell>
  );
}
